import * as THREE from 'three';
import type { Grid2, SurfaceField } from './modSurface';

/**
 * modDecal.ts (M154) — a decal that LIES ON the measured flank (the Kit
 * Number's door numbers). The M128 decal was a flat plane placed from the
 * flank's extent sampled near its centre only: on the rebuilt coupe's door (a
 * scoop under a sharp shoulder crease, a fold toward the sill) the crease cut
 * the top of the "1" and the lower door bulge sliced the bottom of the "0".
 *
 * M154 r2: round 1 bent every vertex of the decal to the flank's envelope;
 * from a three-quarter view the digits then bent with each cell of it (the
 * coupe's and the sports car's "1" folded over, the "0" leaned). Now the
 * decal is ONE SMOOTH SURFACE fitted to the patch of flank it covers — a
 * plane along the car, a gentle curve top to bottom (the door's tumblehome),
 * so the digits stay straight — pushed out until nothing of the flank comes
 * through it. The patch is chosen where that smooth surface lies within
 * `maxFloat` of the flank everywhere (so it never stands off a crease), on
 * unbroken PAINT (no wheel arch, scoop, vent or handle under it) and clear of
 * anything worn on the flank there (side-exit whistles).
 */

/** How far the decal may stand off the flank anywhere (a smooth decal over a
 *  panel that curves more than this would hang off it). */
export const DECAL_MAX_FLOAT = 0.015;
/** The decal's lift over the outermost flank point under it. */
export const DECAL_LIFT = 0.005;
/** The frame of clear paint kept round a door decal where the panel has room. */
export const DECAL_FRAME = 0.05;

/** A box on the flank (z0..z1 along, y0..y1 up) a decal must keep off. */
export interface FlankBox {
  z0: number;
  z1: number;
  y0: number;
  y1: number;
}

/** The smooth surface fitted to a patch of the flank: |x| = c0 + c1 dz +
 *  c2 dy + c3 dy² + c4 dz² + c5 dz dy + c6 dy³ about (zc, yc) (a panel's
 *  curve along the car and its tumblehome, no ripple), plus `shift` (pushed
 *  out so no measured point of the flank stands outside it); `float` = how
 *  far it stands off the flank at worst. */
export interface FlankFit {
  zc: number;
  yc: number;
  c: [number, number, number, number, number, number, number];
  shift: number;
  float: number;
}

/** |x| of the fitted surface at (z, y). */
export function flankFitAt(f: FlankFit, z: number, y: number): number {
  const dz = z - f.zc;
  const dy = y - f.yc;
  return f.c[0] + f.c[1] * dz + f.c[2] * dy + f.c[3] * dy * dy + f.c[4] * dz * dz + f.c[5] * dz * dy + f.c[6] * dy * dy * dy + f.shift;
}

/** Solve the normal equations (Gaussian elimination, partial pivots). */
function solveN(A: number[][], b: number[]): number[] | null {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]!]);
  for (let col = 0; col < n; col++) {
    let piv = col;
    for (let r = col + 1; r < n; r++) if (Math.abs(M[r]![col]!) > Math.abs(M[piv]![col]!)) piv = r;
    if (Math.abs(M[piv]![col]!) < 1e-12) return null;
    [M[col], M[piv]] = [M[piv]!, M[col]!];
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const k = M[r]![col]! / M[col]![col]!;
      for (let c = col; c <= n; c++) M[r]![c]! -= k * M[col]![c]!;
    }
  }
  return M.map((row, i) => row[n]! / row[i]!);
}

/**
 * Fit the smooth decal surface to the flank over the patch centred at (zc,
 * yc), `w` along by `h` tall: least squares over the side silhouette's cells
 * under the patch, then shifted out over every cell's outermost point (the
 * fit's lowest value across the cell). Null when the patch is not all
 * measured.
 */
export function flankFit(surf: SurfaceField, zc: number, yc: number, w: number, h: number): FlankFit | null {
  return gridFit(surf.side, zc, yc, w, h);
}

/**
 * M154 polish: the same smooth fit over ANY of the measured silhouettes (a
 * Grid2 over two car-local axes holding the outermost value per cell): the
 * flank (z, y → |x|) or the top (x, z → y). The fit's `zc` / `yc` are the
 * patch centre on the grid's own two axes.
 */
export function gridFit(g: Grid2, zc: number, yc: number, w: number, h: number): FlankFit | null {
  const z0 = zc - w / 2;
  const z1 = zc + w / 2;
  const y0 = yc - h / 2;
  const y1 = yc + h / 2;
  const i0 = Math.max(0, Math.floor((z0 - g.a0) / g.da));
  const i1 = Math.min(g.na - 1, Math.floor((z1 - g.a0) / g.da));
  const j0 = Math.max(0, Math.floor((y0 - g.b0) / g.db));
  const j1 = Math.min(g.nb - 1, Math.floor((y1 - g.b0) / g.db));
  if (i1 < i0 || j1 < j0) return null;
  const cells: { za: number; zb: number; ya: number; yb: number; v: number }[] = [];
  for (let j = j0; j <= j1; j++) {
    for (let i = i0; i <= i1; i++) {
      const v = g.data[j * g.na + i]!;
      if (Number.isNaN(v)) return null;
      // the part of the cell under the patch
      const za = Math.max(z0, g.a0 + i * g.da);
      const zb = Math.min(z1, g.a0 + (i + 1) * g.da);
      const ya = Math.max(y0, g.b0 + j * g.db);
      const yb = Math.min(y1, g.b0 + (j + 1) * g.db);
      cells.push({ za, zb, ya, yb, v });
    }
  }
  const N = 7;
  const A = Array.from({ length: N }, () => new Array<number>(N).fill(0));
  const b = new Array<number>(N).fill(0);
  for (const c of cells) {
    const dz = (c.za + c.zb) / 2 - zc;
    const dy = (c.ya + c.yb) / 2 - yc;
    const row = [1, dz, dy, dy * dy, dz * dz, dz * dy, dy * dy * dy];
    const wt = Math.max(1e-6, (c.zb - c.za) * (c.yb - c.ya));
    for (let r = 0; r < N; r++) {
      b[r]! += wt * row[r]! * c.v;
      for (let k = 0; k < N; k++) A[r]![k]! += wt * row[r]! * row[k]!;
    }
  }
  // (a whisper of ridge regression keeps a one-row patch solvable)
  for (let r = 1; r < N; r++) A[r]![r]! += 1e-9;
  const sol = solveN(A, b);
  if (!sol) return null;
  const f: FlankFit = { zc, yc, c: [sol[0]!, sol[1]!, sol[2]!, sol[3]!, sol[4]!, sol[5]!, sol[6]!], shift: 0, float: 0 };
  // Pushed out over every cell's outermost point, judged at the cell's
  // middle (the flank's outermost point in a cell sits where the panel
  // bulges, as the fitted surface does; taking the fit's lowest corner
  // instead added the panel's own slope across a cell, a centimetre on a
  // tumblehome, to every decal's stand-off).
  const mid = (c: (typeof cells)[number]): number => flankFitAt(f, (c.za + c.zb) / 2, (c.ya + c.yb) / 2);
  let shift = -Infinity;
  for (const c of cells) shift = Math.max(shift, c.v - mid(c));
  f.shift = shift;
  let float = 0;
  for (const c of cells) float = Math.max(float, mid(c) - c.v);
  f.float = float;
  return f;
}

/** M154 polish r5 (pure; the tests pin it): how much further out the flank
 *  stands under a decal's bottom quarter than under its top quarter (its
 *  outermost |x| there; 0 when either is unmeasured). */
export function flankLean(surf: SurfaceField, z: number, y: number, w: number, h: number): number {
  // (column by column: a flank that leans only at one end of the decal
  // leans that end's strokes)
  let lean = 0;
  for (let i = 0; i < 4; i++) {
    const za = z - w / 2 + (w * i) / 4;
    const zb = za + w / 4;
    const top = surf.sideX(za, zb, y + h / 4, y + h / 2);
    const bot = surf.sideX(za, zb, y - h / 2, y - h / 4);
    if (top !== null && bot !== null) lean = Math.max(lean, Math.abs(bot - top));
  }
  return lean;
}

/** Where a door decal of `w` × `h` (car-local, the +X flank) goes: the
 *  nearest spot to (z, y) where the flank is painted all over the footprint,
 *  no `avoid` box overlaps it (with a hand of margin) and the smooth decal
 *  stands at most DECAL_MAX_FLOAT off the flank anywhere, shrinking the decal
 *  a little if the panel is short; failing the last, the painted spot it
 *  stands closest on; the anchor (and full size) when no surface is in or
 *  nothing qualifies. */
export function flankDecalSpot(
  surf: SurfaceField | null | undefined,
  z: number,
  y: number,
  w: number,
  h: number,
  reach: { dz: number; dy: number } = { dz: 0.6, dy: 0.2 },
  avoid: readonly FlankBox[] = [],
  /** How close behind the flank non-paint geometry may sit and still count
   *  as an opening (SurfaceField.flankPainted). A thin plate (the Formula's
   *  rear-wing end plate, 2 cm) carries the wing's elements against its
   *  inner face: they are behind it, not cut into it. */
  paintDepth = 0.025,
  /** The search grid (metres). M154 polish r3: the bike's tail flank is a
   *  narrow slanted band whose one fitting spot at 80% size fell between the
   *  2 cm grid's points, and its number shrank to 60%. */
  step = 0.02,
  /** (M154 polish r5) How much further out the flank may stand under the
   *  decal's foot than under its top (metres): a flank that rolls inward
   *  over the decal's top leans it from every angled view (the bike's tail,
   *  whose upper flank curves into its top). */
  maxLean = Infinity,
): { z: number; y: number; w: number; h: number } {
  if (!surf) return { z, y, w, h };
  const margin = 0.03;
  let fallback: { z: number; y: number; w: number; h: number; float: number } | null = null;
  for (const k of [1, 0.9, 0.8, 0.7, 0.6]) {
    const ww = w * k;
    const hh = h * k;
    const nz = Math.round(reach.dz / step);
    const ny = Math.round(reach.dy / step);
    // a frame of clear paint round the decal when the panel has room for one
    // (the sports car's number stood against its side intake's lip)
    for (const frame of [DECAL_FRAME, 0]) {
      let best: { z: number; y: number; d: number } | null = null;
      for (let i = -nz; i <= nz; i++) {
        for (let j = -ny; j <= ny; j++) {
          const zz = z + i * step;
          const yy = y + j * step;
          const d = Math.hypot(i * step, 1.5 * j * step);
          if (best && d >= best.d) continue;
          if (avoid.some((b) => zz + ww / 2 + margin > b.z0 && zz - ww / 2 - margin < b.z1 && yy + hh / 2 + margin > b.y0 && yy - hh / 2 - margin < b.y1)) continue;
          if (!surf.flankPainted(zz - ww / 2 - frame, zz + ww / 2 + frame, yy - hh / 2 - frame, yy + hh / 2 + frame, undefined, paintDepth)) continue;
          if (Number.isFinite(maxLean) && flankLean(surf, zz, yy, ww, hh) > maxLean) continue;
          const fit = flankFit(surf, zz, yy, ww, hh);
          if (!fit) continue;
          if (fit.float > DECAL_MAX_FLOAT) {
            // (the full-size spot that stands closest, kept in case none is smooth)
            if (k === 1 && (!fallback || fit.float < fallback.float - 1e-9)) fallback = { z: zz, y: yy, w: ww, h: hh, float: fit.float };
            continue;
          }
          best = { z: zz, y: yy, d };
        }
      }
      if (best) return { z: best.z, y: best.y, w: ww, h: hh };
    }
  }
  if (fallback) return { z: fallback.z, y: fallback.y, w: fallback.w, h: fallback.h };
  return { z, y, w, h };
}

/**
 * The decal's geometry on one flank (`side` +1 = +X, -1 = -X), centred at
 * (zc, yc), `w` along the car by `h` tall, its texture upright and reading
 * front to back as seen from that side: the smooth surface fitted to the
 * flank under it (flankFit), DECAL_LIFT outside the flank's outermost point.
 * Without a surface (or a patch it cannot fit) the decal is a flat plane at
 * `fallbackX`.
 */
export function flankDecalGeometry(
  surf: SurfaceField | null | undefined,
  side: 1 | -1,
  zc: number,
  yc: number,
  w: number,
  h: number,
  fallbackX: number,
  offset = DECAL_LIFT,
  nu = 16,
  nv = 12,
  /** (M154 polish r5) UPRIGHT: each column of the decal stands vertical at
   *  the outermost point of the fitted flank under it, so its strokes stay
   *  vertical where the flank rolls inward (it stands off the flank there by
   *  at most the lean flankDecalSpot allowed). */
  upright = false,
): THREE.BufferGeometry {
  const fit = surf ? flankFit(surf, zc, yc, w, h) : null;
  const columnX = (z: number): number => {
    let x = -Infinity;
    for (let j = 0; j <= nv; j++) x = Math.max(x, flankFitAt(fit!, z, yc - h / 2 + (j / nv) * h));
    return x;
  };
  const pos = new Float32Array((nu + 1) * (nv + 1) * 3);
  const uv = new Float32Array((nu + 1) * (nv + 1) * 2);
  for (let j = 0; j <= nv; j++) {
    for (let i = 0; i <= nu; i++) {
      const u = i / nu;
      const v = j / nv;
      // u reads front to back: toward -Z on the +X flank, +Z on the -X flank
      const z = side > 0 ? zc + w / 2 - u * w : zc - w / 2 + u * w;
      const y = yc - h / 2 + v * h;
      const x = fit ? (upright ? columnX(z) : flankFitAt(fit, z, y)) : fallbackX;
      const k = j * (nu + 1) + i;
      pos[k * 3] = side * (x + offset);
      pos[k * 3 + 1] = y;
      pos[k * 3 + 2] = z;
      uv[k * 2] = u;
      uv[k * 2 + 1] = v;
    }
  }
  const index: number[] = [];
  for (let j = 0; j < nv; j++) {
    for (let i = 0; i < nu; i++) {
      const a = j * (nu + 1) + i;
      const b = a + 1;
      const c = a + nu + 2;
      const d = a + nu + 1;
      index.push(a, b, c, a, c, d);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geo.setIndex(index);
  geo.computeVertexNormals();
  return geo;
}

// ---------------------------------------------------------------------------
// M154 polish: a decal that lies ON the measured TOP surface (the Formula's
// Kit Number on its sidepod tops — the only broad paint the race camera sees
// on that car).
// ---------------------------------------------------------------------------

/** How much longer (along the car) a lying decal is drawn than its texture's
 *  own proportion: the race camera looks down on a near-flat panel at about
 *  30 degrees and foreshortens it to about half, so a true-proportioned
 *  number read as a squashed smear from the chase view. */
export const TOP_DECAL_STRETCH = 1.4;

/** Where a decal of `w` (across the car) × `l` (along it) lies on the top
 *  surface: the largest of a few sizes, at the spot nearest (x, z), where the
 *  surface under the footprint is PAINT (no vent, intake, lamp or glass), is
 *  measured all over and one smooth surface lies within DECAL_MAX_FLOAT of it
 *  everywhere; null when no size fits (or no surface is in).
 *  M154 polish r2: `accept` = a further test a spot must pass (the Formula's
 *  pod number keeps off the gloves standing on the pod and out from under the
 *  cape's resting cloth). */
export function topDecalSpot(
  surf: SurfaceField | null | undefined,
  x: number,
  z: number,
  w: number,
  l: number,
  reach: { dx: number; dz: number } = { dx: 0.08, dz: 0.3 },
  sizes: readonly number[] = [1, 0.9, 0.8, 0.7, 0.6],
  accept?: (spot: { x: number; z: number; w: number; l: number }) => boolean,
): { x: number; z: number; w: number; l: number } | null {
  if (!surf) return null;
  const step = 0.02;
  const nx = Math.round(reach.dx / step);
  const nz = Math.round(reach.dz / step);
  for (const k of sizes) {
    const ww = w * k;
    const ll = l * k;
    let best: { x: number; z: number; d: number } | null = null;
    for (let i = -nx; i <= nx; i++) {
      for (let j = -nz; j <= nz; j++) {
        const xx = x + i * step;
        const zz = z + j * step;
        const d = Math.hypot(i * step, j * step);
        if (best && d >= best.d) continue;
        if (accept && !accept({ x: xx, z: zz, w: ww, l: ll })) continue;
        if (!surf.topPainted(xx - ww / 2, xx + ww / 2, zz - ll / 2, zz + ll / 2)) continue;
        const fit = gridFit(surf.top, xx, zz, ww, ll);
        if (!fit || fit.float > DECAL_MAX_FLOAT) continue;
        best = { x: xx, z: zz, d };
      }
    }
    if (best) return { x: best.x, z: best.z, w: ww, l: ll };
  }
  return null;
}

/**
 * M154 polish r2 (pure; the tests pin it): the share of `pts` (car-local
 * points on a decal) a camera along `d` (unit, toward the camera) sees past
 * the measured top and, when given, `extra` (another surface's height over a
 * car-local (x, z): the cape's cloth). The ray is followed `reach` along the
 * ground at most: what stands beside the decal (an engine cover, a cloth over
 * it) hides it; a thin wing a metre and more away, which the top heightfield
 * reads as solid down to the floor, does not.
 */
export function decalSeenShare(
  surf: SurfaceField | null | undefined,
  pts: readonly THREE.Vector3[],
  d: THREE.Vector3,
  reach = 0.9,
  extra?: ((x: number, z: number) => number | null) | null,
): number {
  if (!pts.length) return 1;
  const h = Math.hypot(d.x, d.z);
  const tMax = h > 1e-6 ? reach / h : 0;
  const q = new THREE.Vector3();
  let seen = 0;
  for (const p of pts) {
    let hidden = false;
    for (let t = 0.03; t <= tMax && !hidden; t += 0.02) {
      q.copy(p).addScaledVector(d, t);
      const top = surf?.topAt(q.x, q.z) ?? null;
      if (top !== null && q.y < top - 0.004) hidden = true;
      const e = extra?.(q.x, q.z) ?? null;
      if (e !== null && q.y < e) hidden = true;
    }
    if (!hidden) seen++;
  }
  return seen / pts.length;
}

/** M154 polish r2: a lying decal fades from full (seen whole) to nothing as
 *  the camera loses DECAL_SEEN_FADE of it: a number is shown whole or not at
 *  all (the Formula's far pod read as a lone '0' from the rear three-quarter
 *  view, the engine cover hiding its '1'). */
export const DECAL_SEEN_FULL = 0.97;
export const DECAL_SEEN_FADE = 0.12;

/** The opacity for a decal the camera sees `share` of (pure). */
export function decalSeenOpacity(share: number): number {
  return Math.max(0, Math.min(1, (share - (DECAL_SEEN_FULL - DECAL_SEEN_FADE)) / DECAL_SEEN_FADE));
}

/**
 * The lying decal's geometry: the smooth surface fitted to the top under it
 * (gridFit over the top heightfield), DECAL_LIFT above its highest point,
 * the texture upright for the race camera (the digit's top toward the nose,
 * reading left to right from behind: u runs toward -X).
 */
export function topDecalGeometry(
  surf: SurfaceField | null | undefined,
  xc: number,
  zc: number,
  w: number,
  l: number,
  fallbackY: number,
  offset = DECAL_LIFT,
  nu = 16,
  nv = 12,
): THREE.BufferGeometry {
  const fit = surf ? gridFit(surf.top, xc, zc, w, l) : null;
  const pos = new Float32Array((nu + 1) * (nv + 1) * 3);
  const uv = new Float32Array((nu + 1) * (nv + 1) * 2);
  for (let j = 0; j <= nv; j++) {
    for (let i = 0; i <= nu; i++) {
      const u = i / nu;
      const v = j / nv;
      const x = xc + w / 2 - u * w;
      const z = zc - l / 2 + v * l;
      const y = fit ? flankFitAt(fit, x, z) : fallbackY;
      const k = j * (nu + 1) + i;
      pos[k * 3] = x;
      pos[k * 3 + 1] = y + offset;
      pos[k * 3 + 2] = z;
      uv[k * 2] = u;
      uv[k * 2 + 1] = v;
    }
  }
  const index: number[] = [];
  for (let j = 0; j < nv; j++) {
    for (let i = 0; i < nu; i++) {
      const a = j * (nu + 1) + i;
      const b = a + 1;
      const c = a + nu + 2;
      const d = a + nu + 1;
      // (wound so the face looks UP)
      index.push(a, b, c, a, c, d);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geo.setIndex(index);
  geo.computeVertexNormals();
  return geo;
}
